/**
 * Export Jurisdiction and Classification U.S. EAR99, India Not Subject to Regulations
 */

import { AttributeInputCallback, CallbackType, Config, FRAuth, FRLoginFailure, FRLoginSuccess, FRStep, StepType } from '@forgerock/javascript-sdk';
import React, { useEffect, useRef, useState } from 'react';
import { configOptionsPWC, trees } from '../../config/forgerockConfig';

import {  useLocation, useNavigate } from 'react-router-dom';
import CallbasedViewGenerator from '../../components/common/CallbasedViewGenerator/CallbasedViewGenerator';
import Loader from '../../components/common/Loader/Loader';
import { MessageType } from '../../interfaces/baseInterface';
import {  checkAndValidatePortalParam, getTree, gotoAndResumeUseEffect, handleFailureStep, handlePortalName, redirectHandle } from '../../utils/FRCallBackUtil';
import "./Login.css";
import { cloneDeep, isEmpty } from 'lodash';
import { useTranslation } from '../../components/common/Translation/translationProvider';
import NotificationPopup from '../../components/common/NotificationPopup/NotificationPopup';
import { redirectionUrl } from '../../utils/FRCallBackUtil';


/**
 * The Login component is the main component for the login page.
 * It retrieves the step from the session storage and renders a CallbasedViewGenerator
 * with the default view. If the step is a login step, it renders a CallbasedViewGenerator
 * with a navigation link to the forgot password page and a button to sign in as an
 * RTX employee.
 * If the step is a login success step, it will redirect the user to the success URL
 * returned by the REST call.
 * If there is no goto value set in state it will check if there is a redirectURI value set in state and if it is not empty.
 * If the redirectURI value is not empty it will redirect the user to the redirectURI.
 * If the redirectURI value is empty it will redirect the user to pageNotFound.
 */
const Login: React.FC<{queryParams:Array<string>}> = (props) => {
  const tree = getTree('login');
  const [step, setStep] = useState<FRStep | FRLoginFailure | FRLoginSuccess | null>(null);
  const navigate = useNavigate()
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [goto, setGoto] = useState('');
  const [redirectURI, setRedirectURI] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<MessageType>(MessageType.info);
  const location = useLocation();
  const { t } = useTranslation();
  const params = new URLSearchParams(location.search);
  const portalParam = params.get('portal')?.toLowerCase();

  const queryParams = new Map()
  props.queryParams?.forEach((value: string) => queryParams.set(value, params.get(value)) );
  const [isModalOpen, setIsModalOpen] = useState(false);
  //For Popup
  const onClick = (e: React.MouseEvent<HTMLParagraphElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'A' || target.parentElement?.tagName === 'A') {
      e.preventDefault();
      setIsModalOpen(true);
    }
  };
  handlePortalName();
 checkAndValidatePortalParam();


  const getRegistrationLink = () => {
    let registrationLink = "";
    if (portalParam === 'supplier') {
      registrationLink = t('common.suppPortalRegLink')

    } else if (portalParam === 'customer') {
      registrationLink = t('common.custPortalRegLink', { custPortalRegLink: import.meta.env.VITE_PWC_CUSTOMER_REGISTER_URL })
    }
    return registrationLink;
  }


  gotoAndResumeUseEffect({ setStep, setGoto, setRedirectURI, setMaintenancePage: () => { }, tree,queryParams });

  const handleNavigateToSAML = () => {
    if (isEmpty(goto)) {
      navigate(`/samlLogin?portal=${portalParam}`);
    } else {
      navigate(`/samlLogin?portal=${portalParam}&goto=${goto}`);
    }

  };

  /**
   * CheckGotoAndRedirect will check if there is a goto value set in  state and if it is valid.
   * If the goto value is valid it will redirect the user to the success URL returned by the REST call.
   * If the goto value is not valid it will redirect the user to pageNotFound.
   * If there is no goto value set in state it will check if there is a redirectURI value set in state and if it is not empty.
   * If the redirectURI value is not empty it will redirect the user to the redirectURI.
   * If the redirectURI value is empty it will redirect the user to pageNotFound.
   */
  

  const CheckGotoAndRedirect = () => {
    sessionStorage.removeItem('step');
    const targetUrl = redirectionUrl(portalParam || '');
    if (goto && goto != null && goto != '' && goto != undefined) {
      fetch(Config.get().serverConfig.baseUrl + "json/realms/root/realms/" + configOptionsPWC.realmPath + "/users?_action=validateGoto", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept-API-Version": "protocol=2.1,resource=3.0",
        },
        body: JSON.stringify({ goto: goto }),
      })
        .then((response) =>
          response.json().then((data) => {
            // clearBrowserData()
            
            window.location.replace(data.successURL)
          })).catch(() => {
            //window.location.replace(portalParam === 'customer' ? import.meta.env.VITE_PWC_CUSTOMER_URL : import.meta.env.VITE_PWC_SUPPLIER_URL);
            window.location.replace(targetUrl);
          })
    } else if (redirectURI && redirectURI != null && redirectURI != '' && redirectURI != undefined) {
      navigate(redirectURI)
    } else {
      //window.location.replace(portalParam === 'customer' ? import.meta.env.VITE_PWC_CUSTOMER_URL : import.meta.env.VITE_PWC_SUPPLIER_URL);
      window.location.replace(targetUrl);
    }

  }
  useEffect(() => {
    if (step) {
      switch (step.type) {
        case StepType.LoginSuccess:
          CheckGotoAndRedirect()
          break;
        case StepType.LoginFailure:
             sessionStorage.removeItem('authIndexValue');
            sessionStorage.removeItem('step');
            sessionStorage.removeItem('realm');
          const failStep = step
          handleFailureStep({
            failStep,
            setStep,
            setIsAlertOpen,
            setMessageType,
            setMessage,
            tree,
            restartOnFailure: true,
            queryParams
          });
          break;

      }

    }

  }, [step]);



  const setEmailMFA = async () => {
    let stepCopy = cloneDeep(step as FRStep);
    stepCopy.getCallbacksOfType(CallbackType.StringAttributeInputCallback).forEach(callback => {
      const attributeInputCallback = new AttributeInputCallback(callback.payload);
      if (attributeInputCallback.getName() === 'setEmailMFA') {
        callback.setInputValue("setEmailMFA");
      }
    })
    setMessage("")
    setIsAlertOpen(false)
    let nextStep = await FRAuth.next(stepCopy as FRStep, { tree });
    setStep(nextStep);
  }

  const resendOtp = async () => {
    let stepCopy = cloneDeep(step as FRStep);
    stepCopy.getCallbackOfType(CallbackType.StringAttributeInputCallback).setInputValue("1");
    setMessage("")
    setIsAlertOpen(false)
    let nextStep = await FRAuth.next(stepCopy as FRStep, { tree });
    setStep(nextStep);
  }


  const getSupplierCompanyRegistrationData = () => {
    const popUpContentData: { header: string, body: string, url?: string }[] = [];
    popUpContentData.push({ header: t('common.supplierPortalCanadaRegHeader'), body: t('common.supplierPortalCanadaRegBody',{supPortalCanadaRegLink:import.meta.env.VITE_PWC_SUPPLIER_REGISTER_URL})});
    popUpContentData.push({ header: t('common.supplierPortalPolandRegHeader'), body: t('common.supplierPortalPolandRegBody') });
    return popUpContentData
  }


  /**
   * This function will return the view for the login page.
   * Depending on the step, it will either display the login form, redirect to the MFA page, or display the password reset form.
   * @returns {JSX.Element} The view for the login page.
   */
  const getLoginView = () => {
    let uiElement = <Loader />;
    if (step && step.type === StepType.Step) {
      sessionStorage.removeItem('authIndexValue');
      sessionStorage.removeItem('step');
      sessionStorage.removeItem('realm');
      const onClickFunctionMap = new Map<string, Function>();
      let additionalBottomElement = <></>;
      switch (step.getStage()) {
        case "RedirectMFA":
          sessionStorage.setItem('step', JSON.stringify(step))
          sessionStorage.setItem('redirectOrgin', window.location.origin + window.location.pathname)
          sessionStorage.setItem('redirectQuery', window.location.search)
          redirectHandle(step, tree);
          return uiElement;
        case "login":
          onClickFunctionMap.set("forgotPasswordLink", () => {
            console.log("function called")
            const gotoString= goto ? '&goto='+goto : ''
            navigate('/reset-password?portal='+portalParam+gotoString );
          });
          additionalBottomElement =
            <div className='bottomElement'>
              <button onClick={handleNavigateToSAML} type='submit' className='employeeLoginButton'>{t('common.empLoginButtonTxt')}</button>
              {/* For popup */}
              {portalParam === 'customer' && (<p dangerouslySetInnerHTML={{ __html: getRegistrationLink() }}></p>)}
              {portalParam === 'supplier' && (<p  onClick={onClick} dangerouslySetInnerHTML={{ __html: getRegistrationLink() }} />)}
              <NotificationPopup isModelOpen={isModalOpen} setIsModalOpen={setIsModalOpen} popUpContentData={getSupplierCompanyRegistrationData()} ></NotificationPopup>

            </div>
          break;
        case "loginCiam":  
         onClickFunctionMap.set("forgotPasswordLink", () => {
            console.log("function called")
            const gotoString= goto ? '&goto='+goto : ''
            navigate('/reset-password?portal='+portalParam+gotoString );
          });
          break;
        case 'RegisterMFA':

          onClickFunctionMap.set("setEmailMFA", setEmailMFA);
          break;
        case "CheckCodeEmail":
          onClickFunctionMap.set("resend", resendOtp);
          break

      }
      uiElement = <CallbasedViewGenerator key={step.getStage() ? step.getStage() : '' + Math.random()} step={step as FRStep} setStep={setStep}
        tree={tree} messageType={messageType} setMessageType={setMessageType}
        message={message} setMessage={setMessage} isAlertOpen={isAlertOpen} setIsAlertOpen={setIsAlertOpen} additionalBottomElement={additionalBottomElement} onClickFunctionMap={onClickFunctionMap} />
    }
    return uiElement;
  }

  return (
    <>{getLoginView()}
    </>
  );


};

export default Login;
